// Executive Cockpit - C-Suite Reliability Dashboard
// Real-time KPIs, trends, and alerts for executive decision-making
// ============================================================

import React, { useState, useEffect } from 'react';
import {
    AlertTriangle,
    CheckCircle,
    Clock,
    DollarSign,
    Activity,
    Target,
    RefreshCw,
    Download,
    Calendar,
    BarChart3,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { KPICard } from '../components/cockpit/KPICard';
import { AlertCard } from '../components/cockpit/AlertCard';

interface KPIMetric {
    id: string;
    title: string;
    value: string | number;
    unit?: string;
    target?: number;
    status: 'good' | 'warning' | 'critical';
    trend: 'up' | 'down' | 'stable';
    trendValue: string;
    icon: React.ReactNode;
    sparklineData?: Array<{ value: number }>;
}

interface AlertItem {
    id: string;
    severity: 'high' | 'medium' | 'low';
    message: string;
    timestamp: string;
    category: string;
}

export const ExecutiveCockpit: React.FC = () => {
    const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d'>('30d');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(new Date());

    // Simulated KPI Data
    const kpiMetrics: KPIMetric[] = [
        {
            id: 'mtbf',
            title: 'MTBF',
            value: 5000,
            unit: 'hrs',
            target: 4500,
            status: 'good',
            trend: 'up',
            trendValue: '+15%',
            icon: <Clock className="w-6 h-6" />,
            sparklineData: [{ value: 4200 }, { value: 4400 }, { value: 4700 }, { value: 4900 }, { value: 5000 }],
        },
        {
            id: 'availability',
            title: 'Availability',
            value: 99.8,
            unit: '%',
            target: 99.0,
            status: 'good',
            trend: 'up',
            trendValue: '+0.3%',
            icon: <Target className="w-6 h-6" />,
            sparklineData: [{ value: 99.3 }, { value: 99.5 }, { value: 99.6 }, { value: 99.7 }, { value: 99.8 }],
        },
        {
            id: 'warranty',
            title: 'Warranty Cost',
            value: '65',
            unit: 'K',
            target: 60,
            status: 'warning',
            trend: 'up',
            trendValue: '+8%',
            icon: <DollarSign className="w-6 h-6" />,
            sparklineData: [{ value: 55 }, { value: 58 }, { value: 60 }, { value: 63 }, { value: 65 }],
        },
        {
            id: 'open8d',
            title: 'Open 8D Reports',
            value: 12,
            unit: '',
            target: 10,
            status: 'critical',
            trend: 'up',
            trendValue: '+3',
            icon: <AlertTriangle className="w-6 h-6" />,
            sparklineData: [{ value: 8 }, { value: 9 }, { value: 10 }, { value: 11 }, { value: 12 }],
        },
        {
            id: 'fracas',
            title: 'Open FRACAS',
            value: 25,
            unit: '',
            target: 20,
            status: 'warning',
            trend: 'stable',
            trendValue: '0',
            icon: <Activity className="w-6 h-6" />,
            sparklineData: [{ value: 26 }, { value: 25 }, { value: 25 }, { value: 24 }, { value: 25 }],
        },
        {
            id: 'mttr',
            title: 'MTTR',
            value: 2.3,
            unit: 'hrs',
            target: 2.5,
            status: 'good',
            trend: 'down',
            trendValue: '-8%',
            icon: <Clock className="w-6 h-6" />,
            sparklineData: [{ value: 2.8 }, { value: 2.6 }, { value: 2.5 }, { value: 2.4 }, { value: 2.3 }],
        },
    ];

    const mtbfTrendData = [
        { month: 'Jan', mtbf: 4200, target: 4500 },
        { month: 'Feb', mtbf: 4400, target: 4500 },
        { month: 'Mar', mtbf: 4700, target: 4500 },
        { month: 'Apr', mtbf: 4900, target: 4500 },
        { month: 'May', mtbf: 5000, target: 4500 },
    ];

    const failureDistribution = [
        { name: 'Motor', value: 35, color: '#EF4444' },
        { name: 'Seal', value: 25, color: '#F59E0B' },
        { name: 'Bearing', value: 15, color: '#10B981' },
        { name: 'Pump', value: 15, color: '#3B82F6' },
        { name: 'Other', value: 10, color: '#6B7280' },
    ];

    const warrantyCostData = [
        { month: 'Jan', parts: 25, labor: 15, shipping: 8, admin: 5 },
        { month: 'Feb', parts: 28, labor: 16, shipping: 9, admin: 5 },
        { month: 'Mar', parts: 30, labor: 18, shipping: 10, admin: 6 },
        { month: 'Apr', parts: 32, labor: 19, shipping: 10, admin: 6 },
        { month: 'May', parts: 35, labor: 20, shipping: 11, admin: 7 },
    ];

    const alerts: AlertItem[] = [
        { id: '1', severity: 'high', message: 'MTBF dropped 5% in Product Line A - Investigate motor failures', timestamp: '2 hours ago', category: 'Reliability' },
        { id: '2', severity: 'high', message: '8D-042 overdue by 7 days - Containment action pending', timestamp: '5 hours ago', category: '8D' },
        { id: '3', severity: 'medium', message: 'Warranty claims +12% this month vs forecast', timestamp: '1 day ago', category: 'Warranty' },
        { id: '4', severity: 'medium', message: '15 FRACAS incidents awaiting root cause assignment', timestamp: '1 day ago', category: 'FRACAS' },
    ];

    useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(() => {
                setLastUpdate(new Date());
            }, 60000);
            return () => clearInterval(interval);
        }
    }, [autoRefresh]);

    const handleExport = () => {
        alert('Exporting Executive Report to PDF...');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Executive Cockpit 🎯</h1>
                        <p className="text-gray-600 dark:text-gray-400">Real-time reliability metrics for strategic decision-making</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex gap-1 bg-white dark:bg-slate-800 rounded-lg p-1 shadow-sm">
                            {(['24h', '7d', '30d', '90d'] as const).map((range) => (
                                <button
                                    key={range}
                                    onClick={() => setTimeRange(range)}
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${timeRange === range ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                >
                                    {range.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`p-2 rounded-lg transition-all ${autoRefresh ? 'bg-green-600 text-white' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-400'}`}
                        >
                            <RefreshCw className={`w-5 h-5 ${autoRefresh ? 'animate-spin' : ''}`} />
                        </button>

                        <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                            <Download className="w-4 h-4" /> Export Report
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="w-4 h-4" />
                    Last updated: {lastUpdate.toLocaleTimeString()} • {lastUpdate.toLocaleDateString()}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {kpiMetrics.map((metric) => <KPICard key={metric.id} metric={metric} />)}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">MTBF Trend vs Target</h3>
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={mtbfTrendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="mtbf" stroke="#10B981" strokeWidth={3} name="Actual MTBF" />
                            <Line type="monotone" dataKey="target" stroke="#6B7280" strokeWidth={2} strokeDasharray="5 5" name="Target" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Top Failure Modes</h3>
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie data={failureDistribution} cx="50%" cy="50%" labelLine={false} label={(entry) => `${entry.name} (${entry.value}%)`} outerRadius={80} fill="#8884d8" dataKey="value">
                                {failureDistribution.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-slate-700 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Warranty Cost Breakdown</h3>
                        <DollarSign className="w-5 h-5 text-amber-600" />
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={warrantyCostData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="parts" stackId="a" fill="#3B82F6" name="Parts" />
                            <Bar dataKey="labor" stackId="a" fill="#10B981" name="Labor" />
                            <Bar dataKey="shipping" stackId="a" fill="#F59E0B" name="Shipping" />
                            <Bar dataKey="admin" stackId="a" fill="#6B7280" name="Admin" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        Critical Alerts & Action Items
                    </h3>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                        {alerts.filter(a => a.severity === 'high').length} High Priority
                    </span>
                </div>

                <div className="space-y-3">
                    {alerts.map((alert) => <AlertCard key={alert.id} alert={alert} />)}
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <QuickActionButton icon={<BarChart3 className="w-6 h-6 mb-2" />} title="Advanced Analytics" subtitle="Weibull, MTBF, FTA" color="from-blue-600 to-blue-700" />
                <QuickActionButton icon={<CheckCircle className="w-6 h-6 mb-2" />} title="8D Reports" subtitle="Review open items" color="from-green-600 to-green-700" />
                <QuickActionButton icon={<AlertTriangle className="w-6 h-6 mb-2" />} title="FRACAS" subtitle="Active incidents" color="from-red-600 to-red-700" />
                <QuickActionButton icon={<DollarSign className="w-6 h-6 mb-2" />} title="Warranty" subtitle="Cost forecast" color="from-amber-600 to-amber-700" />
            </div>
        </div>
    );
};

const QuickActionButton: React.FC<{ icon: React.ReactNode, title: string, subtitle: string, color: string }> = ({ icon, title, subtitle, color }) => (
    <button className={`p-4 bg-gradient-to-r ${color} text-white rounded-xl hover:opacity-90 transition-all shadow-lg text-left`}>
        {icon}
        <div className="font-bold">{title}</div>
        <div className="text-xs opacity-90">{subtitle}</div>
    </button>
);

export default ExecutiveCockpit;
